<template>
  <GhostComponent
    :title="title"
    component-name-when-active="PopperCardButton"
    slot-to-render-when-inactive="btn"
    placement="right"
    strategy="absolute"
    style="display: inline-block"
  >
    <template #btn>
      <BaseLabel basic class="m-r-5 f-11" :text="`#${localId}`" @click="readyToLoadData = true" />
    </template>
    <template #popper>
      <div v-if="$apollo.loading" class="text-center loader">
        <div class="preloader m-20 m-b-0 pls-blue pls-center">
          <svg class="pl-circular" viewBox="25 25 50 50">
            <circle class="plc-path" cx="50" cy="50" r="20"></circle>
          </svg>
        </div>
      </div>
      <template v-if="loaded">
        <template
          v-if="
            hire.dateStart &&
            hire.dateEnd &&
            moment(hire.dateStart).isValid() &&
            moment(hire.dateEnd).isValid()
          "
        >
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Date from') }}:</strong>
            </div>
            <div class="col-sm-6 nowrap">
              {{ moment(hire.dateStart).format('MMM D, YYYY') }}
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Date to') }}:</strong>
            </div>
            <div class="col-sm-6 nowrap">
              {{ moment(hire.dateEnd).format('MMM D, YYYY') }}
            </div>
          </div>
        </template>

        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Rate') }}:</strong>
          </div>
          <div class="col-sm-6 tw-leading-4">
            {{ hire.currency }} {{ hire.rateFormatted }} {{ $t(hire.ratetypeName) }}
          </div>
        </div>
        <hr class="tw-mt-3 tw-mb-3" />
        <div class="row">
          <div class="col-sm-6">
            <strong>{{ $t('Payment terms') }}</strong>
          </div>
          <div class="col-sm-6">
            {{ hire.paymentTerm }} {{ $t('days') }}
            {{ hire.paymentTermMethod === 'net' ? $t('Net (accounting)') : $t('end of month') }}.
          </div>
        </div>

        <div v-if="hire.hasFactoring" class="row">
          <div class="col-sm-7">
            <small>
              {{
                $t(
                  "The freelancer's bills will be paid after :days days given that it has been approved.",
                  { days: hire.factoringDays },
                )
              }}
            </small>
          </div>
        </div>

        <template v-if="hire.bills.length > 0">
          <hr class="tw-mt-3 tw-mb-3" />
          <div class="row">
            <div class="col-sm-7">
              <strong>{{ $t('Totally billed') }}</strong>
              <br />
              <small class="c-gray">
                {{ $t(`Excl. fees and ${vatTerminology}`) }}
              </small>
            </div>
            <div class="col-sm-5">{{ hire.currency }}&nbsp;{{ hire.totalBilled.inDec }}</div>
          </div>
          <div class="row">
            <div class="col-sm-7">
              <strong>{{ $t('Totally invoiced') }}</strong>
              <br />
              <small class="c-gray">
                {{ $t(`Excl. ${vatTerminology}.`) }}
                {{ $t('Excl. unapproved bills') }}
              </small>
            </div>
            <div class="col-sm-5">{{ hire.currency }}&nbsp;{{ hire.totalInvoiced.inDec }}</div>
          </div>
        </template>

        <template v-if="hire.hasRecruiter">
          <hr class="tw-mt-3 tw-mb-3" />
          <strong>
            {{ $t('Attributed recruiter') }}
          </strong>
          <div class="f-500">{{ hire.recruiter.name }}</div>
          <span class="iblock f-13">
            <span v-if="!hire.recruiterManagesWorkers">
              {{ $t('Fee') }}: {{ hire.recruiterFeeInPercent }} %
              <br />
            </span>

            <template v-if="hire.recruiterOwnershipDays <= 0">
              <span class="c-black p-l-5 p-r-5 f-12 f-500 iblock bgm-indigo-100">
                {{
                  $t('Ownership period of :days days has expired', {
                    days: hire.recruiterOwnershipDays,
                  })
                }}
              </span>

              {{ $t('Ownership period') }}: {{ hire.recruiterOwnershipDays }}
              {{ 'days' }}
              <span class="c-gray">({{ hire.recruiterOwnershipDaysLeft }} {{ $t('left') }})</span>
            </template>

            <br />
          </span>
        </template>
        <template v-if="account.isOrganization">
          <hr class="tw-mt-3 tw-mb-3" />
          <div class="row">
            <div class="col-sm-12">
              <strong>{{ $t('Company') }}</strong>
              <br />
              {{ hire.company.name }}
            </div>
          </div>
        </template>
        <template v-if="hire.company.hasTeamMembers">
          <hr class="tw-mt-3 tw-mb-3" />
          <div class="row">
            <div class="col-sm-12">
              <strong>{{ $t('Job owners') }}</strong>
              <br />
              <span v-if="!hire.job.owners?.length" class="c-gray small">
                {{ $t('No owner specified') }}
              </span>
              <GhostComponent
                v-for="owner in hire.job.owners"
                :key="owner.globalId"
                :global-id="owner.globalId"
                component-name-when-active="ProfileInfoCard"
              >
                <div>{{ owner.name }}</div>
              </GhostComponent>
            </div>
          </div>
        </template>
        <template v-if="hire.ir35StatusForDisplay">
          <hr class="tw-mt-3 tw-mb-3" />
          <div class="row">
            <div class="col-sm-6">
              <strong>
                <img
                  class="i-logo"
                  src="/gfx/ir35-logo-indigo.svg"
                  style="width: 52px; display: inline-block"
                />
              </strong>
            </div>
            <div class="col-sm-6">
              <div v-if="hire.ir35StatusForDisplay === 'OUTSIDE'" class="f-11 ucase f-700 c-black">
                Outside IR35
              </div>
              <div
                v-else-if="hire.ir35StatusForDisplay === 'INSIDE'"
                class="f-11 ucase f-700 text-warning"
              >
                Inside IR35
              </div>
              <div
                v-else-if="hire.ir35StatusForDisplay === 'NEW'"
                class="f-500 text-warning f-10 ucase iblock"
              >
                Awaiting acceptance
              </div>
              <a
                :href="`/sds/${hire.sds.localId}/pdf`"
                :title="$t('Download SDS as PDF')"
                target="_blank"
                class="iblock m-t-5"
              >
                <span class="glyphicon glyphicon-file" aria-hidden="true" />
                {{ $t('Download SDS') }}
              </a>
            </div>
          </div>
        </template>

        <hr class="tw-mt-3 tw-mb-3" />
        <div class="row">
          <div class="col-sm-12">
            <strong>{{ $t('Contract Legal Entity') }}</strong>
            <div>{{ hire.companyNameOnContract }}</div>
            <div class="tw-mb-2">{{ hire.companyAddressOnContract }}</div>
          </div>
        </div>

        <hr class="tw-mt-3 tw-mb-2" />
        <WdsStack justify-space-between align-center>
          <a
            :href="`/contract/${hire.localId}/pdf`"
            :title="$t('Download the contract as a PDF')"
            target="_blank"
          >
            <WdsButtonGhost secondary>
              <WdsStack align-center gap-xs>
                <WdsIcon name="file" :size="16" />
                {{ $t('Download contract') }}
              </WdsStack>
            </WdsButtonGhost>
          </a>
          <a
            v-if="account.isCompany && threadId"
            :title="$t('Find bills, messages, reviews and more in the conversation.')"
            :href="`/messages/${threadId}`"
          >
            <WdsButtonGhost secondary>
              {{ $t('Conversation') }}
            </WdsButtonGhost>
          </a>
        </WdsStack>
      </template>
    </template>
  </GhostComponent>
</template>

<script>
import gql from 'graphql-tag'
import moment from 'moment'
import { WdsStack, WdsButtonGhost, WdsIcon } from '@worksome/wds-components'
import GhostComponent from '@/components/GhostComponent.vue'
import BaseLabel from '@/components/BaseLabel.vue'

const HIRE_FRAGMENT = gql`
  fragment PopoverHireFragment on Hire {
    localId
    dateStart
    dateEnd
    currency
    rateFormatted
    ratetypeName
    paymentTerm
    paymentTermMethod
    hasFactoring
    factoringDays
    hasRecruiter
    recruiterFeeInPercent
    recruiterManagesWorkers
    recruiterOwnershipDays
    recruiterOwnershipDaysLeft
    ir35StatusForDisplay
    threadId
    companyNameOnContract
    companyAddressOnContract
    totalBilled {
      inDec
    }
    totalInvoiced {
      inDec
    }
    recruiter {
      globalId
      name
    }
    company {
      globalId
      name
      hasTeamMembers
    }
    job: yjob {
      globalId
      localId
      owners {
        globalId
        name
      }
    }
    sds {
      localId
    }
    bills(first: 100000) {
      data {
        globalId
      }
    }
  }
`

export default {
  apollo: {
    hire: {
      skip() {
        return !this.readyToLoadData
      },
      query: gql`
        query Hire($localId: Int!) {
          me {
            globalId
            activeAccount {
              globalId
              ... on HasHires {
                hire(localId: $localId) {
                  globalId
                  localId
                  ...PopoverHireFragment
                }
              }
            }
          }
        }
        ${HIRE_FRAGMENT}
      `,
      update(data) {
        this.loaded = true
        return data?.me?.activeAccount?.hire
      },
      variables() {
        return {
          localId: this.localId,
        }
      },
      // fetchPolicy: 'cache-only',
    },
  },
  fragment: HIRE_FRAGMENT,
  components: { BaseLabel, GhostComponent, WdsStack, WdsIcon, WdsButtonGhost },
  props: {
    localId: {
      type: Number,
      default: 0,
    },
    globalId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      readyToLoadData: false,
      loaded: false,
      hire: {},
    }
  },
  computed: {
    title() {
      if (this.loaded && this.hire) {
        return `${$t('Contract')} #${this.hire.localId}`
      }

      return $t('Contract')
    },
    threadId() {
      return this.hire?.threadId
    },
    moment: () => moment,
    vatTerminology() {
      return vatTerminology
    },
  },
}
</script>

<style scoped></style>
